import logNewRelicCustom from './logNewRelicCustom';
import { NEW_RELIC_EVENTS } from '../constants';

const NO_VALUE_KEY = 'No Value';

const getSKUAtributeValue = (sku, attributeName) => {
  switch (attributeName.toLowerCase()) {
  case 'category':
    return sku.categories?.length > 0
      ? sku.categories[sku.categories.length - 1]
      : NO_VALUE_KEY;
  default:
    return sku.keyProductFeatures.find((spec) => spec.name === attributeName)?.value ?? NO_VALUE_KEY;
  }
};

const splitSkusByAttribute = (skuList, attributeName) => {
  const skusWithValue = skuList.map((sku) => ({
    ...sku,
    value: getSKUAtributeValue(sku, attributeName)
  }));
  const values = [];
  skusWithValue.forEach((skuValue) => {
    if (!values.includes(skuValue.value)) {
      values.push(skuValue.value);
    }
  });
  return values.map((value) => ({
    value,
    skus: skusWithValue.filter((skuValue) => skuValue.value === value)
  }));
};

const createNodeValue = (value, children, sku) => ({
  value,
  children,
  sku: sku ? { itemId: sku.itemId, parentId: sku.parentId } : null
});

/// Root and recursing function to generate a node tree of attribute options
/// Tree splits the input SKUs by attributes in the order received
///   I.E. Attribute at index 1 will always be an earlier node than index 2+
/// Should generate a tree where all leaf nodes are options with choices that each represent a single SKU
/// Returns either a tree starting at the indicated index of the attributes or null
/// NULL is returned when:
///   The requested node has 5 parents already
///   There is no attribute to check against (at end of list)
///   There are no SKUs to split
///   The current attribute and all further attributes in the list could not find a useful difference
///      I.E. this attribute and all following are either not on the SKUs or all values match
export function generateAttributeTree(skuList, attributeList, currentAttrIndex = 0, depth = 0) {
  if (depth === 5 || currentAttrIndex === attributeList.length || skuList.length < 1) {
    return null;
  }

  const { name: attributeName } = attributeList[currentAttrIndex];
  const defaultSku = skuList[0];
  let i = 0;

  // With one sku:
  // if we are in the tree something went wrong
  // if we're just starting the set only has 1 item, return a short tree
  if (skuList.length === 1) {
    if (depth === 0) {
      const value = getSKUAtributeValue(defaultSku, attributeName);
      return {
        name: attributeName,
        values: [createNodeValue(value, null, defaultSku)]
      };
    }
    return null;
  }

  // Note: splitSkusByAttribute should always return at least 1 item in the array.
  // Either it splits into multiple values or it doesn't.
  // if it doesn't either everything is clustered under 1 value or 1 value of "no value"
  const valuesWithSKUs = splitSkusByAttribute(skuList, attributeName);
  const noValueItems = valuesWithSKUs.find((skuValue) => skuValue.value === NO_VALUE_KEY);
  const isNotSplit = valuesWithSKUs.length === 0 || valuesWithSKUs.length === 1;
  const hasNoValue = noValueItems && noValueItems.skus.length > 0;

  // if we didn't split well on this node
  if (isNotSplit || hasNoValue) {
    const secondaryTree = generateAttributeTree(skuList, attributeList, currentAttrIndex + 1, depth);

    // Use a better, lower node's tree instead
    if (secondaryTree) {
      return secondaryTree;
    }

    // We didn't split and our children didn't split
    // Then we need to return an error or default
    if (isNotSplit) {
      // Percolate the lack of splitting up to the root node as needed
      // Upper nodes will trim the tree if the did split regardless of "No Value" entries
      if (currentAttrIndex !== 0) {
        return null;
      }

      // When at the root node look for the first attribute with a value to show.
      for (i = 0; i < attributeList.length; i += 1) {
        const { name: returnAttributeName } = attributeList[i];
        const value = getSKUAtributeValue(defaultSku, returnAttributeName);
        if (value !== NO_VALUE_KEY) {
          return {
            name: returnAttributeName,
            values: [createNodeValue(value, null, defaultSku)]
          };
        }
      }

      // If somehow we don't find a attribute with a value show a "No Value"
      return {
        name: attributeName,
        values: [createNodeValue(NO_VALUE_KEY, null, defaultSku)]
      };
    }
  }

  // At this point we have either:
  //   A good split
  //   A split with "No Value" as a choice, but no replacement tree lower

  // Otherwise explore the node we have and expand it's leaves
  for (i = 0; i < valuesWithSKUs.length; i += 1) {
    const skuValue = valuesWithSKUs[i];
    // Generate tree will be null if depth is too far, no attribute is available, or this is only 1 item
    skuValue.children = generateAttributeTree(skuValue.skus, attributeList, currentAttrIndex + 1, depth + 1);
  }

  return {
    name: attributeName,
    values: valuesWithSKUs
      .sort((valA, valB) => (valA.value.toLowerCase() < valB.value.toLowerCase() ? -1 : 1))
      .map((skuValue) => {
        const leafSku = skuValue.children === null ? skuValue.skus[0] : null;

        if (skuValue.children === null && skuValue.skus.length > 1) {
          logNewRelicCustom(NEW_RELIC_EVENTS.NO_DIFFERENCE_TO_SSKU, { attributeName, skuValue });

        }

        return createNodeValue(
          skuValue.value,
          skuValue.children,
          leafSku
        );
      })
  };
}